import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ChevronRight, ExpandMore, Add, MoreHoriz, DragIndicator, Undo, Unarchive } from '../icons';
import { useCreatePage } from '../../hooks/usePage';
import useHandlePopoverClick from '../../hooks/useHandlePopoverClick';
import {
  reorderPages,
  movePage,
  restorePage,
  fetchPages,
  updatePageReorderOptimistic,
  updatePageMoveOptimistic,
  updatePageRestoreOptimistic,
  archivePage,
  updatePageUserSettings
} from '../../redux/Slices/pageSlice';
import { showError, getErrorMessage } from '../../utils/snackbarHelpers';
import './scss/PageSidebarItem.scss';

// Atlaskit Drag-and-Drop
import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
  attachClosestEdge,
  extractClosestEdge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";

/**
 * Recursive sidebar item for nested pages with DnD support
 */
const PageSidebarItem = ({ page, allPages, workspaceId, level = 0, isTrash = false, isArchived = false, section = 'sidebar', onMoveClick }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pageId: activePageId } = useParams();
  const createPageMutation = useCreatePage();
  const { handlePopoverClick } = useHandlePopoverClick();
  const itemRef = useRef(null);

  // Status for DnD
  const [isDragging, setIsDragging] = useState(false);
  const [closestEdge, setClosestEdge] = useState(null);
  const [isDropTarget, setIsDropTarget] = useState(false);

  // Get page from Redux store (which has optimistic updates)
  const pageFromRedux = useSelector(state => state.pages.byId[page.shortId]);
  const currentPage = pageFromRedux || page;

  // Use expansion state from Redux (per-user)
  const isExpanded = currentPage.isExpanded || false;
  const [isHovered, setIsHovered] = useState(false);

  // Find and sort child pages
  const childPages = allPages
    .filter(p => p.parentId === page._id && !p.deletedAt)
    .sort((a, b) => {
      const field = section === 'favorites' ? 'favoriteOrder' : 'order';
      const orderA = a[field] ?? 0;
      const orderB = b[field] ?? 0;
      if (orderA !== orderB) return orderA - orderB;
      return new Date(a.createdAt) - new Date(b.createdAt);
    });
  const hasChildren = childPages.length > 0;

  const isActive = activePageId === page.shortId;

  const handleClick = () => {
    if (isTrash || isArchived) return;
    navigate(`/p/${page.shortId}`);
  };

  // Keyboard activation for the (div-based) row so it's reachable without a mouse.
  const handleKeyDown = (e) => {
    if (isTrash || isArchived) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate(`/p/${page.shortId}`);
    }
  };

  const handleToggleExpand = (e) => {
    e.stopPropagation();
    dispatch(updatePageUserSettings({
      pageId: currentPage.shortId,
      isExpanded: !isExpanded
    }));
  };

  const handleAddSubPage = async (e) => {
    e.stopPropagation();
    try {
      const pageData = {
        title: '',
        icon: '📄',
        workspaceId: page.workspaceId?.shortId || page.workspaceId || workspaceId || null,
        parentId: page._id,
        type: 'doc'
      };
      const result = await createPageMutation.mutateAsync(pageData);
      
      // Expand the parent page after creating sub-page
      dispatch(updatePageUserSettings({
        pageId: currentPage.shortId,
        isExpanded: true
      }));

      if (result?.page?.shortId) {
        navigate(`/p/${result.page.shortId}`);
      }
    } catch (error) {
      console.error('Failed to create sub-page:', error);
    }
  };

  const handleRestore = async (e) => {
    e.stopPropagation();
    try {
      await dispatch(restorePage({ pageId: page.shortId })).unwrap();
      // Update UI only after server success
      dispatch(updatePageRestoreOptimistic({ pageId: page.shortId }));
    } catch (error) {
      console.error('Failed to restore page:', error);
    }
  };

  const handleUnarchive = async (e) => {
    e.stopPropagation();
    try {
      await dispatch(archivePage({ pageId: page.shortId, archived: false })).unwrap();
    } catch (error) {
      console.error('Failed to unarchive page:', error);
    }
  };

  const handleMoreOptions = (e) => {
    e.stopPropagation();
    const effectiveWorkspaceId = page?.workspaceId?.shortId || page?.workspaceId || workspaceId || null;
    handlePopoverClick(e, 'pageActionsMenu', {
      page,
      workspaceId: effectiveWorkspaceId,
      onMove: onMoveClick ? () => onMoveClick(page) : undefined,
      isClosed: false,
      isTrash
    });
  };

  // DnD Setup
  useEffect(() => {
    const element = itemRef.current;
    if (!element || isTrash || isArchived) return;

    const combined = combine(
      draggable({
        element,
        getInitialData: () => ({ type: 'PAGE', pageId: page.shortId, page, level }),
        onDragStart: () => setIsDragging(true),
        onDrop: () => setIsDragging(false),
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) => {
          if (source.data.type !== 'PAGE') return false;
          if (source.data.pageId === page.shortId) return false;

          // Circular reference check
          const isDescendant = (parentId, childId) => {
            let current = allPages.find(p => p.shortId === childId);
            while (current) {
              if (current.shortId === parentId) return true;
              current = allPages.find(p => p.shortId === (current.parentId?.shortId || current.parentId));
            }
            return false;
          };
          if (isDescendant(source.data.pageId, page.shortId)) return false;
          return true;
        },
        getData: ({ input, element }) => {
          return attachClosestEdge({ type: 'PAGE', pageId: page.shortId, page }, {
            input,
            element,
            allowedEdges: ["top", "bottom"],
          });
        },
        onDragEnter: ({ self }) => {
          setIsDropTarget(true);
          setClosestEdge(extractClosestEdge(self.data));
        },
        onDrag: ({ self }) => setClosestEdge(extractClosestEdge(self.data)),
        onDragLeave: () => {
          setIsDropTarget(false);
          setClosestEdge(null);
        },
        onDrop: async ({ source, self }) => {
          setIsDropTarget(false);
          setClosestEdge(null);

          const edge = extractClosestEdge(self.data);
          const sourcePageId = source.data.pageId;
          const targetPageId = page.shortId;
          const sourcePage = allPages.find(p => p.shortId === sourcePageId);

          if (!sourcePage) return;

          try {
            if (!edge) {
              // DROP ON: Make it a child
              dispatch(updatePageMoveOptimistic({ pageId: sourcePageId, newParent: page._id }));
              await dispatch(movePage({ pageId: sourcePageId, newParent: page._id })).unwrap();
              dispatch(updatePageUserSettings({
                pageId: currentPage.shortId,
                isExpanded: true
              }));
            } else {
              // REORDER: Above or Below
              // 1. Determine the relevant list of siblings
              const siblings = allPages.filter(p => {
                if (section === 'favorites') return p.isFavorite && !p.deletedAt && !p.archived;
                
                return p.parentId === page.parentId &&
                !p.deletedAt &&
                // If workspaceId is relevant for root level filtering
                (!page.parentId ? p.workspaceId === page.workspaceId : true);
              }).sort((a, b) => {
                const field = section === 'favorites' ? 'favoriteOrder' : 'order';
                return (a[field] ?? 0) - (b[field] ?? 0);
              });

              const targetIndex = siblings.findIndex(p => p.shortId === targetPageId);
              let newOrder;

              const getOrder = (p) => (section === 'favorites' ? p.favoriteOrder : p.order) ?? 0;

              if (edge === 'top') {
                const prev = siblings[targetIndex - 1];
                if (!prev) {
                  newOrder = getOrder(siblings[0]) - 1000;
                } else {
                  newOrder = (getOrder(prev) + getOrder(siblings[targetIndex])) / 2;
                }
              } else {
                const next = siblings[targetIndex + 1];
                if (!next) {
                  newOrder = getOrder(siblings[siblings.length - 1]) + 1000;
                } else {
                  newOrder = (getOrder(siblings[targetIndex]) + getOrder(next)) / 2;
                }
              }

              // 2. Perform optimistic update and API calls
              // First move it to the parent if it's changing parents (or just ensure it's correct)
              let newParentId = page.parentId?._id || page.parentId || null;

              if (sourcePage.parentId !== newParentId) {
                dispatch(updatePageMoveOptimistic({ pageId: sourcePageId, newParent: newParentId }));
                await dispatch(movePage({ pageId: sourcePageId, newParent: newParentId })).unwrap();
              }

              // Then reorder
              // Optimistic update handled by thunk/reducer
              await dispatch(reorderPages({ pageId: sourcePageId, newOrder, section })).unwrap();
            }
          } catch (error) {
            // The optimistic move/reorder already mutated the tree. Roll back to
            // server truth by refetching, so a failed drag doesn't leave the page
            // stuck in the wrong place, and tell the user why.
            console.error('Failed to move/reorder page:', error);
            const effectiveWorkspaceId = page?.workspaceId?.shortId || page?.workspaceId || workspaceId || null;
            if (effectiveWorkspaceId) {
              dispatch(fetchPages({ workspaceId: effectiveWorkspaceId }));
            }
            showError(dispatch, getErrorMessage(error, "Couldn't move the page. Please try again."));
          }
        },
      })
    );
    return () => combined();
  }, [page, allPages, isTrash, dispatch, workspaceId, currentPage.shortId, isArchived, level]);

  return (
    <div
      className={`page-sidebar-item ${isDragging ? 'dragging' : ''} ${isDropTarget ? 'drop-target' : ''} ${closestEdge ? `drop-${closestEdge}` : ''}`}
      ref={itemRef}
    >
      <div
        className={`page-sidebar-item-content${isActive ? ' active' : ''}${isTrash ? ' trash' : ''}${isArchived ? ' archived' : ''} ${page._optimistic ? 'ghost' : ''}`}
        // style={{ paddingLeft: `${level * 20 + 52}px` }}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={isTrash || isArchived ? -1 : 0}
        aria-current={isActive ? 'page' : undefined}
        title={currentPage.title || 'Untitled'}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {
          !isTrash && !isArchived && (
            <div className="page-sidebar-drag-handle">
              <DragIndicator size={24} />
            </div>
          )
        }

        {hasChildren ? (
          <button className="page-sidebar-item-toggle" onClick={handleToggleExpand}>
            {isExpanded ? <ExpandMore size={16} /> : <ChevronRight size={16} />}
          </button>
        ) : (
          <div className="page-sidebar-item-toggle-spacer" />
        )}

        <span className="page-sidebar-item-icon">{currentPage.icon || '📄'}</span>
        <span className="page-sidebar-item-title">{currentPage.title || 'Untitled'}</span>

        {isHovered && (
          <div className="page-sidebar-item-actions">
            {isTrash && (
              <button
                id={`page-sidebar-item-action-btn-restore-${page._id}`}
                className="page-sidebar-item-action-btn restore-btn"
                onClick={handleRestore}
                data-tooltip-content="Restore page"
              >
                <Undo size={16} />
              </button>
            )}
            {isArchived && (
              <button
                id={`page-sidebar-item-action-btn-unarchive-${page._id}`}
                className="page-sidebar-item-action-btn unarchive-btn"
                onClick={handleUnarchive}
                data-tooltip-content="Unarchive page"
              >
                <Unarchive size={18} />
              </button>
            )}
            <button
              id={`page-sidebar-item-action-btn-more-${page._id}`}
              className="page-sidebar-item-action-btn"
              onClick={handleMoreOptions}
              data-popover-trigger
              data-tooltip-content="More options"
            >
              <MoreHoriz size={18} />
            </button>
          </div>
        )}
      </div>

      {isExpanded && hasChildren && (
        <div className="page-sidebar-item-children">
          {childPages.map(childPage => (
            <PageSidebarItem
              key={childPage._id}
              page={childPage}
              allPages={allPages}
              workspaceId={workspaceId}
              level={level + 1}
              isTrash={isTrash}
              section={section}
              onMoveClick={onMoveClick}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PageSidebarItem;

